import { PROJECT_COLORS, type Project } from '@taim/shared';
import { type FormEvent, useEffect, useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ColorPicker, FormError, NativeSelect } from './components';
import { useClients, useCreateProject, useUpdateProject } from './hooks';

const NEW_CLIENT = '__new__';

/** Client dropdown: "No client", the active clients (plus the current one), and optionally "Create new client". */
export function ClientSelect({
  id,
  value,
  onChange,
  allowCreate = false,
  disabled,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  allowCreate?: boolean;
  disabled?: boolean;
}) {
  const clients = useClients({ archived: 'all' });
  const options = (clients.data ?? []).filter((c) => !c.archived || c.id === value);
  return (
    <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">No client</option>
      {options.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
      {allowCreate && <option value={NEW_CLIENT}>Create new client</option>}
    </NativeSelect>
  );
}

/**
 * The client fields of a project create/update. "Create new client" sends `newClientName`, so the
 * API creates the client and the project together (no orphaned client if the project fails).
 */
export function clientFields(choice: string, newClientName: string) {
  return choice === NEW_CLIENT ? { newClientName } : { clientId: choice || null };
}

/** Clockify's "Create new project" dialog, also used to edit a project from the list. */
export function ProjectDialog({
  open,
  project,
  onClose,
  onSaved,
  allowNewClient = true,
}: {
  open: boolean;
  /** Edit this project; create a new one when null. */
  project: Project | null;
  onClose: () => void;
  onSaved?: (p: Project) => void;
  /** Offer "Create new client" (needs catalog:manage). */
  allowNewClient?: boolean;
}) {
  const ids = { name: useId(), client: useId(), newClient: useId(), public: useId() };
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState('');
  const [newClient, setNewClient] = useState('');
  const [color, setColor] = useState<string>(PROJECT_COLORS[0]);
  const [isPublic, setIsPublic] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const create = useCreateProject();
  const update = useUpdateProject();

  useEffect(() => {
    if (!open) return;
    setName(project?.name ?? '');
    setClientId(project?.clientId ?? '');
    setNewClient('');
    setColor(project?.color ?? PROJECT_COLORS[0]);
    setIsPublic(project?.isPublic ?? true);
    setError(null);
  }, [open, project]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const input = { name, ...clientFields(clientId, newClient), color, isPublic };
      const saved = project
        ? await update.mutateAsync({ id: project.id, ...input })
        : await create.mutateAsync(input);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const title = project ? 'Edit project' : 'Create new project';
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor={ids.name}>Project name</Label>
              <Input
                id={ids.name}
                placeholder="Enter project name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor={ids.client}>Client</Label>
              <ClientSelect
                id={ids.client}
                value={clientId}
                onChange={setClientId}
                allowCreate={allowNewClient}
              />
            </div>
          </div>
          {clientId === NEW_CLIENT && (
            <div className="grid gap-2">
              <Label htmlFor={ids.newClient}>New client name</Label>
              <Input id={ids.newClient} value={newClient} onChange={(e) => setNewClient(e.target.value)} />
            </div>
          )}
          <div className="grid gap-2">
            <span className="font-medium text-sm">Colour</span>
            <ColorPicker value={color} onChange={setColor} />
          </div>
          <div className="flex items-center gap-2">
            <Switch id={ids.public} checked={isPublic} onCheckedChange={setIsPublic} />
            <Label htmlFor={ids.public}>Public</Label>
          </div>
          <FormError error={error} />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !name.trim() || (clientId === NEW_CLIENT && !newClient.trim())}
            >
              {project ? 'SAVE' : 'CREATE'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
